import { LoadingState } from '../ui';
// src/pages/SeasonLeaderboard.jsx

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import Confetti from "react-confetti";
import { useWindowSize } from "@react-hook/window-size";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import { useNavigate } from "react-router-dom";
import { Button as FcButton, Card as FcCard, Table as FcTable } from '../ui';

export default function SeasonLeaderboard({ session, userRole, username }) {
  const navigate = useNavigate();
  // Logout handler
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };
  const { competitionId } = useParams();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [winnerId, setWinnerId] = useState(null);
  const [competitionName, setCompetitionName] = useState("");
  const [width, height] = useWindowSize();
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [friendIds, setFriendIds] = useState(new Set());

  useEffect(() => {
    const fetchLeaderboard = async () => {
      // Fetch user's friends
      if (session?.user?.id) {
        const { data: friendshipsData } = await supabase
          .from('friendships')
          .select('user1_id, user2_id')
          .or(`user1_id.eq.${session.user.id},user2_id.eq.${session.user.id}`);

        const friendIdSet = new Set();
        friendshipsData?.forEach(friendship => {
          if (friendship.user1_id === session.user.id) {
            friendIdSet.add(friendship.user2_id);
          } else {
            friendIdSet.add(friendship.user1_id);
          }
        });
        setFriendIds(friendIdSet);
      }

      const { data: competitionData } = await supabase
        .from("competitions")
        .select("id, name, status, knockout_winner")
        .eq("id", competitionId)
        .single();

      if (competitionData?.knockout_winner && competitionData?.status !== "inactive") {
        setWinnerId(competitionData.knockout_winner);
      }
      if (competitionData?.name) {
        setCompetitionName(competitionData.name);
      }

      const statsView =
        competitionData?.status === "inactive"
          ? "user_competition_stats_all_view"
          : "user_competition_stats_view";

      // Use server-side aggregated per-user stats view which now includes username
      const { data, error } = await supabase
        .from(statsView)
        .select("*")
        .eq("competition_id", competitionId)
        .order("rank", { ascending: true });

      let rows = data || [];
      if (error && statsView !== "user_competition_stats_view") {
        const { data: fallbackRows } = await supabase
          .from("user_competition_stats_view")
          .select("*")
          .eq("competition_id", competitionId)
          .order("rank", { ascending: true });
        rows = fallbackRows || [];
      }

      // Exclude entries awaiting approval / rejected from the public leaderboard.
      rows = rows.filter((r) => !['pending', 'rejected'].includes(r.selection_status));

      if (competitionData?.status === "inactive" && rows.length > 0) {
        setWinnerId(rows[0]?.user_id || null);
      }

      setLeaderboard(rows);
      setLoading(false);
    };

    fetchLeaderboard();
  }, [competitionId]);

  // Helper: render username with friend badge
  const renderUsername = (userId, username) => {
    const isFriend = friendIds.has(userId);
    return (
      <span className="flex items-center gap-1">
        {username}
        {isFriend && <span className="text-lg" title="Friend">🤝</span>}
      </span>
    );
  };

  // Multi-entry: distinguish a user's extra entries (entry 1 shows the plain name).
  const nameFor = (entry) =>
    entry.entry_label || (entry.entry_index > 1 ? `${entry.username} (${entry.entry_index})` : entry.username);

  // Find logged in user's entry
  const userEntry = leaderboard.find((u) => u.user_id === session?.user?.id);

  // Pagination logic
  const totalPages = Math.ceil(leaderboard.length / pageSize);
  const paginatedLeaderboard = leaderboard.slice((page - 1) * pageSize, page * pageSize);

  // Table for user standings (matches competition table columns)
  function UserStandingTable({ entry }) {
    if (!entry) return null;
    return (
      <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4 mb-6 border border-ui-border w-full flex flex-col items-center">
        <h3 className="font-semibold text-brand-text mb-2 w-full text-left">Your Standings</h3>
        <div className="overflow-x-auto w-full flex justify-center">
          <FcTable label="Your standings" className="w-full border-collapse text-sm" >
            <thead className="bg-ui-green text-white">
              <tr>
                <th scope="col" className="border p-2 text-left whitespace-nowrap">Rank</th>
                <th scope="col" className="border p-2 text-left whitespace-nowrap min-w-[140px]">Player Name</th>
                <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">Wins</th>
                <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">Draws</th>
                <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">Loses</th>
                <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">GF</th>
                <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">GA</th>
                <th scope="col" className="border p-2 text-center whitespace-nowrap">Points</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border p-2 text-left whitespace-nowrap font-semibold">{entry.rank ? `${entry.rank}${ordinalSuffix(entry.rank)}` : '-'}</td>
                <td className="border p-2 text-left whitespace-nowrap min-w-[140px]">{renderUsername(entry.user_id, nameFor(entry))}</td>
                <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.wins}</td>
                <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.draws}</td>
                <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.losses}</td>
                <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.goals_for}</td>
                <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.goals_against}</td>
                <td className="border p-2 text-center whitespace-nowrap font-bold">{entry.points}</td>
              </tr>
            </tbody>
          </FcTable>
        </div>
      </FcCard>
    );
  }

  function ordinalSuffix(n) {
    if (!n) return '';
    const s = ["th", "st", "nd", "rd"], v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 fc-workspace">
          <LoadingState label="Loading leaderboard..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-canvas">
      {/* Sticky green banner for mobile */}
      <div className="block md:hidden w-full bg-ui-green py-3 px-4 sticky top-0 z-40">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 w-full fc-workspace">
        <h1 className="mb-6 text-brand-text">
          Season Leaderboard{competitionName ? ` - ${competitionName}` : ""}
        </h1>

        {winnerId && leaderboard.length > 0 && (
          <>
            <Confetti
              width={width}
              height={height}
              numberOfPieces={150}
              gravity={0.25}
              tweenDuration={5000}
              recycle={false}
            />
            <div className="mb-4 text-2xl font-bold text-brand-success bg-brand-success-bg p-4 rounded shadow text-center">
              🎉 Congratulations {leaderboard.find((u) => u.user_id === winnerId)?.username ?? "Unknown"}! 🎊
            </div>
          </>
        )}

        {/* User's standings table */}
        <UserStandingTable entry={userEntry} />

        {/* Competition Standings Table */}
        <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow p-4 border border-ui-border w-full flex flex-col items-center">
          <div className="flex items-center justify-between w-full mb-2">
            <h3 className="font-semibold text-brand-text">Competition Standings</h3>
            <Link
              to={`/competition/${competitionId}`}
              className="font-semibold text-sm hover:underline focus:underline focus:outline-none px-2 py-1 rounded transition-colors"
              style={{ color: '#BA6830' }}
            >
              View Competition
            </Link>
          </div>
          <div className="overflow-x-auto w-full flex justify-center">
            <FcTable label="Season leaderboard" className="w-full border-collapse text-sm" >
              <thead className="bg-ui-green text-white">
                <tr>
                  <th scope="col" className="border p-2 text-left whitespace-nowrap">Rank</th>
                  <th scope="col" className="border p-2 text-left whitespace-nowrap min-w-[140px]">Player Name</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">Wins</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">Draws</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">Loses</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">GF</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap hidden md:table-cell">GA</th>
                  <th scope="col" className="border p-2 text-center whitespace-nowrap">Points</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeaderboard.map((entry, idx) => (
                  <tr key={entry.entry_id || entry.user_id} className={idx % 2 ? "bg-gray-50" : ""}>
                    <td className="border p-2 text-left whitespace-nowrap font-semibold">{entry.rank ? `${entry.rank}${ordinalSuffix(entry.rank)}` : '-'}</td>
                    <td className="border p-2 text-left whitespace-nowrap min-w-[140px]">{renderUsername(entry.user_id, nameFor(entry))}</td>
                    <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.wins}</td>
                    <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.draws}</td>
                    <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.losses}</td>
                    <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.goals_for}</td>
                    <td className="border p-2 text-center whitespace-nowrap hidden md:table-cell">{entry.goals_against}</td>
                    <td className="border p-2 text-center whitespace-nowrap font-bold">{entry.points}</td>
                  </tr>
                ))}
              </tbody>
            </FcTable>
          </div>
          {/* Pagination Controls */}
          <div className="flex justify-center items-center mt-4 gap-2">
            <FcButton variant="tertiary" type="submit"
              className=""
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </FcButton>
            <span className="mx-2 text-brand-text font-semibold">Page {page} of {totalPages}</span>
            <FcButton variant="tertiary" type="submit"
              className=""
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </FcButton>
          </div>
        </FcCard>
      </div>
    </div>
  );
}
